import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { APIErrorResponse } from "features/api/utils/apiUtils.ts";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { ValidateProductsRequestBody } from "types/api/products/postValidate.ts";
import { removeImportErrorFromLineItem } from "pages/OrderPages/utils/orderListUtil.ts";
import { lineItemIdSymbol, useValidateOrderRequestMutation } from "features/api/newOrderApi.ts";
import { updateOrderDetails } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { getNewOrderURL } from "utils/string.ts";
import { LineItemErrors } from "components/NewOrders/BuildNewOrderLineItems/BuildNewOrderLineItems.tsx";
import useTimer from "./useTimer.ts";
import { useDispatch, useSelector } from "react-redux";
import { models } from "types/api/viewModels.ts";
import { RootState } from "stores/application.store.tsx";
import { useUserLocation } from "./useUserLocation.ts";
import { useNavigate, useParams } from "react-router";
import { EditedText } from "constants/text.ts";

interface LineItemImportError {
	lineItemError: models["ImportErrorViewModel"] | null;
	modificationsError: models["ImportErrorViewModel"][];
}

interface useGeneratePriceEstimateProps {
	setLineItemMode?: Dispatch<SetStateAction<"other" | "base" | "special" | "revise">>;
	cqData?: models["CustomQuoteViewModel"];
	revisionGlobals?: models["GlobalsViewModel"];
	configs?: models["PendingOrderConfigurationViewModel"][] | undefined;
	setUpdatedItems?: Dispatch<SetStateAction<models["PendingOrderConfigurationViewModel"][] | undefined>>;
	isRevisionOfRevision?: boolean;
}

export const useGeneratePriceEstimate = ({
	setLineItemMode,
	cqData,
	revisionGlobals,
	configs,
	setUpdatedItems,
	isRevisionOfRevision
}: useGeneratePriceEstimateProps) => {
	const [errorBannerType, setErrorBannerType] = useState<string[] | undefined>(undefined);
	const [postError, setPostError] = useState<APIErrorResponse | undefined>(undefined);
	const [lineItemErrors, setLineItemErrors] = useState<LineItemErrors>({});
	const [extendedPrice, setExtendedPrice] = useState<number | undefined>(undefined);
	const [lineItemImportErrors, setLineItemImportErrors] = useState<LineItemImportError[]>([]);
	const errorBannerElement = useRef<HTMLDivElement>(null);
	const scrollToErrorBanner = () => {
		errorBannerElement?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
	};

	useEffect(() => {
		if (errorBannerType && errorBannerType.length > 0) {
			setTimeout(() => {
				errorBannerElement?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
			}, 100);
		}
	}, [errorBannerType]);

	const addedLineItemWithPrice = useRef<models["PendingLineItemViewModel"] | undefined>(undefined);
	const addedGlobals = useRef<models["PendingOrderConfigurationViewModel"] | undefined>(undefined);

	const dispatch = useDispatch();
	const params = useParams();
	const navigateTo = useNavigate();
	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const { isAddOnPage: isAddOn, isCustomColorPage: isCustomColor, isNewQuotePage } = useUserLocation();

	const [validateOrderRequest, { isLoading: isValidateLoading }] = useValidateOrderRequestMutation();

	const { start: startSlowLoadTimer } = useTimer({ timeout: 5000 });

	const handleGeneratePriceEstimate = async (
		items: models["PendingOrderConfigurationViewModel"][] | undefined,
		revisionId: number | undefined = undefined
	) => {
		startSlowLoadTimer();
		setErrorBannerType(undefined);
		setPostError(undefined);
		dispatch(newOrderActions.clearDraftError());
		setTimeout(() => window.scrollTo(0, 0), 0);
		const apiInput: ValidateProductsRequestBody = items ?? [];
		let cqConfig: models["PendingOrderConfigurationViewModel"][] | undefined = undefined;

		if (cqData) {
			const cqLineItem = items?.[0]?.lineItems?.[0];
			const newModifications: models["PendingModificationViewModel"][] =
				cqLineItem?.modifications?.map((modification, index) => ({
					...modification,
					lineItemNumber: Number(`${cqLineItem.lineItemNumber}.${index + 1}`)
				})) ?? [];

			const newLineItem: models["PendingLineItemViewModel"] = {
				...cqLineItem,
				modifications: newModifications
			};
			const globalsToUse =
				cqData?.revisions?.filter((r) => r.revisionId == revisionId)?.[0]?.globals ?? cqData?.globals;
			cqConfig = [
				{
					accountId: cqData?.accountId,
					billToId: cqData?.billToId,
					globals: globalsToUse,
					lineItems: [newLineItem]
				}
			];
		}

		try {
			await validateOrderRequest(cqConfig ?? apiInput)
				.unwrap()
				.then(async (response) => {
					dispatch(newOrderActions.addValidatedOrder(response));
					if (setUpdatedItems) {
						response?.configurations &&
							setUpdatedItems(removeImportErrorFromLineItem(response.configurations));
					}
					const isConfigValid = response?.configurations?.find((config) => {
						if (!config.validationResult?.isValid) {
							setErrorBannerType(["NewOrderConfigurationErrorBanner"]);
						}
						return config.validationResult?.isValid;
					});
					let hasError = false;
					const foundLineItemError =
						response?.configurations?.reduce((accum: LineItemErrors, config) => {
							// eslint-disable-next-line sonarjs/no-nested-functions
							config?.lineItems?.forEach((lineItem) => {
								const validationResults =
									config?.lineItemValidationResults?.[lineItem?.lineItemNumber ?? ""];
								if (validationResults !== undefined && !validationResults?.isValid) {
									hasError = true;
								}
								if (lineItem[lineItemIdSymbol] && validationResults?.errorMessage) {
									accum[lineItem[lineItemIdSymbol]] = validationResults?.errorMessage;
								}
							});
							return accum;
						}, {}) ?? {};

					if (foundLineItemError && hasError) {
						setLineItemErrors(foundLineItemError);
						setErrorBannerType(["NewOrderLineItemErrorBanner"]);
						setLineItemMode?.(isRevisionOfRevision ? "revise" : "base");
						scrollToErrorBanner();
					} else setLineItemErrors({});
					if (cqData && !hasError && isConfigValid) {
						addedLineItemWithPrice.current = response?.configurations?.[0]?.lineItems?.[0];
						addedGlobals.current = response?.configurations?.[0];
						setLineItemMode?.("special");
						setExtendedPrice(response?.pricing?.extendedPrice);
					}

					const draftOrderWithPricing = {
						...draftOrder,
						pricing: {
							...response?.pricing,
							finalNetPrice: response?.pricing?.extendedNetPrice ?? response?.pricing?.netPrice
						}
					};

					if (isConfigValid && !hasError && !cqData) {
						updateOrderDetails(items, dispatch, draftOrderWithPricing, isAddOn, params.orderId);
						navigateTo(
							getNewOrderURL(
								isAddOn,
								isCustomColor,
								isNewQuotePage,
								"view-price-estimates",
								params.orderId
							)
						);
					}
				});
		} catch (error: any) {
			scrollToErrorBanner();
			if (error.status === 400) {
				if ("errors" in error && error.errors[0].code === 40001) {
					setErrorBannerType(["NewOrder40001ErrorBanner"]);
				} else {
					setErrorBannerType(["NewOrder400ErrorBanner"]);
				}
				setPostError(error);
			}

			if (error.status === 502) {
				setPostError(error);
				setErrorBannerType(["NewOrder502ErrorBanner"]);
			}
		}
	};

	useEffect(() => {
		const result: LineItemImportError[] = [];

		const filteredModErrors = (modifications: models["PendingModificationViewModel"][]) =>
			modifications.map((mod) => mod.importError).filter((err): err is models["ImportErrorViewModel"] => !!err);

		configs?.forEach((config: models["PendingOrderConfigurationViewModel"]) => {
			const lineItems = config.lineItems ?? [];
			lineItems.forEach((lineItem: models["PendingLineItemViewModel"]) => {
				const lineItemError = lineItem.importError ?? null;
				const modificationErrors = filteredModErrors(lineItem.modifications ?? []);
				if (lineItemError || modificationErrors.length > 0) {
					result.push({
						lineItemError,
						modificationsError: modificationErrors
					});
				}
			});
		});

		const filteredByEditedText = result.filter((error) => error.lineItemError?.message === EditedText);

		setLineItemImportErrors((prev) => {
			if (result.length > 0 && result.length !== filteredByEditedText.length) {
				setErrorBannerType(["NewOrderLineItemImportErrorBanner"]);
			} else if (
				result.length === filteredByEditedText.length &&
				prev.length > 0 &&
				Object.values(lineItemErrors).filter((value) => value !== EditedText).length === 0
			) {
				setErrorBannerType([]);
				return [];
			}
			return result;
		});
	}, [configs, lineItemErrors, setErrorBannerType]);

	return {
		postError,
		lineItemErrors,
		errorBannerType,
		isValidateLoading,
		handleGeneratePriceEstimate,
		setErrorBannerType,
		setLineItemErrors,
		addedLineItemWithPrice,
		addedGlobals,
		extendedPrice,
		lineItemImportErrors,
		errorBannerElement,
		scrollToErrorBanner
	};
};
